import { useState, useEffect } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { Plus, Battery, Zap, Moon, ChevronLeft, Edit3, ExternalLink, Copy } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import AddActivityForm from './AddActivityForm';
import EditActivityForm from './EditActivityForm';
import AvatarOrInitial from '../Family/AvatarOrInitial';
import '../../styles/design-system.css';

export default function ActivityLibrary() {
  const { currentUser, family, activities, markActivityViewed } = useFamilyContext();
  const location = useLocation();
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [selectedKid, setSelectedKid] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  
  const isParent = currentUser?.isParent;
  const kidActivities = activities[selectedKid] || [];

  // Initialize selectedKid when data is available
  useEffect(() => {
    if (!selectedKid) {
      if (currentUser?.isParent) {
        // For parents, default to viewing their own activities first
        setSelectedKid(currentUser.id);
      } else if (currentUser?.id) {
        // For kids, only show their own activities
        setSelectedKid(currentUser.id);
      }
    }
  }, [currentUser, family, selectedKid]);

  // Debug logging
  console.log('ActivityLibrary Debug:', {
    currentUser,
    family,
    activities,
    selectedKid,
    kidActivities,
    activitiesKeys: Object.keys(activities || {}),
    familyId: family?.id
  });
  
  useEffect(() => {
    // Mark all new activities as viewed for current kid
    if (!isParent && currentUser?.id) {
      kidActivities.forEach(activity => {
        if (activity.isNew) {
          markActivityViewed(currentUser.id, activity.id);
        }
      });
    }
  }, [kidActivities, currentUser, isParent, markActivityViewed]);

  useEffect(() => {
    // Open Add Activity modal if redirected with state or query param
    if ((location.state && location.state.openAdd) || (location.search && location.search.includes('add=1'))) {
      setShowAddForm(true);
    }
  }, [location]);

  const filteredActivities = kidActivities.filter(activity => 
    filterCategory === 'all' || activity.category === filterCategory
  );

  const categories = [...new Set(kidActivities.map(a => a.category))];
  
  // Helper function to check if a kid can edit an activity
  const canKidEditActivity = (activity) => {
    if (isParent) return true; // Parents can edit any activity
    return (
      activity.kidId === currentUser?.id &&  // Belongs to this kid
      activity.isCustom === true              // Is a custom activity (not system default)
    );
  };
  
  const getEnergyIcon = (level) => {
    switch(level) {
      case 'high': return <Zap className="w-4 h-4" />;
      case 'medium': return <Battery className="w-4 h-4" />;
      case 'low': return <Moon className="w-4 h-4" />;
      default: return null;
    }
  };

  // Determine who can add activities
  const canAddActivity = isParent || !isParent; // Both parents and kids can add activities
  
  // Get the display name for the selected user
  const getSelectedUserDisplayName = () => {
    if (selectedKid === currentUser?.id) {
      return isParent ? 'My Activities' : 'Your Activities';
    }
    const selectedUser = family?.kids?.find(k => k.id === selectedKid);
    return selectedUser ? `${selectedUser.name}'s Activities` : 'Activities';
  };



  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header Section */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            {/* Back button, title, and add button */}
            <div className="flex items-center gap-4">
              <Link 
                to="/dashboard" 
                className="p-2 -ml-2 text-stone-600 hover:text-stone-900 hover:bg-stone-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-stone-900">Activity Library</h1>
                <p className="text-sm text-stone-600">{getSelectedUserDisplayName()}</p>
              </div>
              
              {/* Add Activity Button - Adjacent to title */}
              {canAddActivity && (
                <button 
                  onClick={() => setShowAddForm(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">Add Activity</span>
                  <span className="sm:hidden">Add</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="library-container">
        {/* Compact User Selector with Avatars */}
        {isParent && family && (
          <div className="mb-6">
            <div className="text-sm font-medium text-stone-700 mb-3">View activities for:</div>
            <div className="flex items-center gap-3 flex-wrap">
              {/* Parent's own activities */}
              <button
                onClick={() => setSelectedKid(currentUser.id)}
                className={`relative flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                  selectedKid === currentUser.id 
                    ? 'border-primary-500 bg-primary-50 text-primary-700' 
                    : 'border-stone-200 bg-white text-stone-600 hover:border-stone-300'
                }`}
              >
                <AvatarOrInitial
                  avatar={currentUser?.avatar}
                  name={currentUser?.name}
                  size="sm"
                />
                <span className="font-medium">You</span>
                {(activities[currentUser.id] || []).filter(a => a.isNew).length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {(activities[currentUser.id] || []).filter(a => a.isNew).length}
                  </span>
                )}
              </button>
              
              {/* Kids' activities */}
              {family.kids.map(kid => {
                const kidNewCount = (activities[kid.id] || []).filter(a => a.isNew).length;
                return (
                  <button
                    key={kid.id}
                    onClick={() => setSelectedKid(kid.id)}
                    className={`relative flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                      selectedKid === kid.id 
                        ? 'border-primary-500 bg-primary-50 text-primary-700' 
                        : 'border-stone-200 bg-white text-stone-600 hover:border-stone-300'
                    }`}
                  >
                    <AvatarOrInitial
                      avatar={kid?.avatar}
                      name={kid?.name}
                      size="sm"
                    />
                    <span className="font-medium">{kid.name}</span>
                    {kidNewCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                        {kidNewCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Category Filter */}
        <div className="mb-6">
          <div className="text-sm font-medium text-stone-700 mb-3">Filter by category:</div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilterCategory('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterCategory === 'all' 
                  ? 'bg-stone-900 text-white' 
                  : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
              }`}
            >
              All Activities
              <span className="ml-2 text-xs opacity-75">({kidActivities.length})</span>
            </button>
            {categories.map(category => {
              const count = kidActivities.filter(a => a.category === category).length;
              return (
                <button
                  key={category}
                  onClick={() => setFilterCategory(category)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filterCategory === category 
                      ? 'bg-stone-900 text-white' 
                      : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                  }`}
                >
                  {category}
                  <span className="ml-2 text-xs opacity-75">({count})</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Activities Grid */}
        <div className="library-activities-grid">
          {filteredActivities.map(activity => (
            <div
              key={activity.id}
              className={`library-activity-card ${activity.energyLevel}-energy`}
            >
              <div className="library-card-header">
                <div className="flex-1">
                  <div className="library-activity-name">{activity.name}</div>
                  <div className="library-activity-meta">
                    <div className="library-energy-indicator">
                      {getEnergyIcon(activity.energyLevel)}
                      <span className="capitalize">{activity.energyLevel} Energy</span>
                    </div>
                    <span className="library-category-tag">
                      {activity.category}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {activity.isNew && !isParent && (
                    <span className="px-2 py-1 bg-magenta-100 text-magenta-700 text-xs font-medium rounded-full">
                      NEW
                    </span>
                  )}
                  
                  {/* Edit button for parents, or for kids who can edit */}
                  {(isParent || canKidEditActivity(activity)) && (
                    <button
                      onClick={() => setEditingActivity(activity)}
                      className="library-edit-btn"
                      title={isParent ? "Edit activity" : "Edit my activity"}
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                  )}
                  
                  {/* Duplicate button for kids when activity is shared or not editable */}
                  {!isParent && !canKidEditActivity(activity) && activity.isCustom && (
                    <button
                      onClick={() => {
                        // Create a duplicate activity for this kid
                        setEditingActivity({
                          ...activity,
                          id: null, // Clear ID to create new
                          name: `${activity.name} (My Version)`,
                          isDuplicate: true
                        });
                      }}
                      className="library-edit-btn"
                      title="Make my own copy"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              
              {activity.description && (
                <div className="mb-3">
                  <p className="text-sm text-stone-600">{activity.description}</p>
                </div>
              )}
              
              {activity.howToStart && (
                <div className="mt-4 p-3 bg-stone-50 rounded-lg">
                  <p className="text-sm text-stone-700">{activity.howToStart}</p>
                </div>
              )}
              
              {activity.links && Array.isArray(activity.links) && activity.links.length > 0 && (
                <div className="mt-3 space-y-1">
                  <p className="text-xs font-medium text-stone-700">Resources:</p>
                  {activity.links.map((link, index) => (
                    <a
                      key={index}
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-primary-600 hover:text-primary-700 text-xs transition-colors"
                    >
                      <ExternalLink className="w-3 h-3 mr-1" />
                      Link {index + 1}
                    </a>
                  ))}
                </div>
              )}
              
              {activity.isCustom && (
                <div className="mt-3 text-xs text-stone-500">
                  Added by {activity.addedBy === 'system' ? 'default' : activity.addedBy === 'user' ? 'you' : family?.parentName}
                </div>
              )}
            </div>
          ))}
        </div>

        {filteredActivities.length === 0 && (
          <div className="library-empty-state">
            <h3>No activities found</h3>
            <p>No activities found in this category.</p>
            {canAddActivity && (
              <button 
                onClick={() => setShowAddForm(true)}
                className="library-create-first-btn"
              >
                Create First Activity
              </button>
            )}
          </div>
        )}
      </main>

      {/* Add Activity Form Modal */}
      {showAddForm && (
        <AddActivityForm
          selectedKid={selectedKid}
          kidName={family?.kids.find(k => k.id === selectedKid)?.name}
          onClose={() => setShowAddForm(false)}
        />
      )}

      {/* Edit Activity Form Modal */}
      {editingActivity && (
        <EditActivityForm
          activity={editingActivity}
          kidId={selectedKid}
          kidName={family?.kids.find(k => k.id === selectedKid)?.name}
          onClose={() => setEditingActivity(null)}
        />
      )}
    </div>
  );
}